import type { M2MStructure, RelationType } from '#layers/data-model/types/relation';
import type { ResolvedMonospacePrimitiveField, ResolvedMonospaceSingleConstraint, ResolvedMonospaceSingleRelationField } from '#layers/data-model/types/schema/resolved-schema';
import { isNil } from '#layers/base/public';
import { WorkspaceCollectionIds } from '#layers/data-model/constants/schema/collection-ids';

/**
 * Detects if a relation field is part of an M2M relation.
 *
 * M2M detection relies on `oppositeJunctionRelationFieldId` in the endpoint metas.
 * This field links to the junction's "other leg" - enabling traversal of the full M2M structure.
 */
export function isM2M(field: ResolvedMonospaceSingleRelationField): boolean {
	return !isNil(field.meta?.oppositeJunctionRelationFieldId);
}

/**
 * Gets the complete M2M structure from any endpoint field.
 *
 * The traversal uses the meta linking:
 * - field.oppositeRelationField = junction's M2O to this side
 * - field.meta.oppositeJunctionRelationFieldMeta.field = junction's M2O to other side
 * - That field's oppositeRelationField = the other endpoint's O2M
 *
 * @example
 * // From Articles.authors field:
 * const structure = getM2MStructure(authorsField);
 * // structure.junction.toSource = ArticlesAuthors.article
 * // structure.junction.toTarget = ArticlesAuthors.author
 * // structure.target.field = Authors.articles
 */
export function getM2MStructure(field: ResolvedMonospaceSingleRelationField): M2MStructure {
	if (!isM2M(field)) {
		throw new Error(`Field "${field.apiName}" is not an M2M relation (missing oppositeJunctionRelationFieldId in meta)`);
	}

	const junctionToSource = field.oppositeRelationField;

	if (!junctionToSource) {
		throw new Error(`M2M field "${field.apiName}" is missing junction relation (oppositeRelationField)`);
	}

	const junctionToTargetMeta = field.meta?.oppositeJunctionRelationFieldMeta;

	if (!junctionToTargetMeta?.field) {
		throw new Error(`M2M field "${field.apiName}" is missing opposite junction relation meta`);
	}

	const junctionToTarget = junctionToTargetMeta.field;
	const targetField = junctionToTarget.oppositeRelationField;

	if (!targetField) {
		throw new Error(`M2M field "${field.apiName}" is missing target endpoint field`);
	}

	return {
		source: {
			field,
			collectionId: field.collectionId,
		},
		junction: {
			collectionId: junctionToSource.collectionId,
			toSource: junctionToSource,
			toTarget: junctionToTarget,
		},
		target: {
			field: targetField,
			collectionId: targetField.collectionId,
		},
	};
}

export function inferRelationType(relationField: ResolvedMonospaceSingleRelationField): RelationType {
	// Check for M2M first
	if (isM2M(relationField)) {
		return 'm2m';
	}

	if (relationField.isList) {
		return 'o2m';
	}
	else if (relationField.oppositeRelationField?.isList) {
		return 'm2o';
	}
	else {
		return 'o2o';
	}
}

export function isRelationToMonospaceFiles(field: ResolvedMonospaceSingleRelationField) {
	if (field.oppositeCollectionId === WorkspaceCollectionIds.MonospaceFile) return true;

	// For an m2m relation the source field points at the junction, not the file
	// collection, so walk the structure to its ultimate target.
	if (!isM2M(field)) return false;

	try {
		return getM2MStructure(field).target.collectionId === WorkspaceCollectionIds.MonospaceFile;
	}
	catch {
		return false;
	}
}

export function getRelationConstraint(relationField: ResolvedMonospaceSingleRelationField) {
	const thisConstraint = relationField.constraint;
	const thisSideConstrained = !!thisConstraint;
	const oppositeConstraint = relationField.oppositeRelationField?.constraint;
	const oppositeSideConstrained = !!oppositeConstraint;
	const constraint = thisConstraint ?? oppositeConstraint;
	const constrainedCollection = thisSideConstrained ? relationField.collection : relationField.oppositeCollection;
	const referencedCollection = !thisSideConstrained ? relationField.collection : relationField.oppositeCollection;

	return {
		thisSideConstrained,
		oppositeSideConstrained,
		constrainedCollection,
		referencedCollection,
		constraint,
	};
}

/**
 * Get the constrained and referenced fields of a constraint.
 */
export function getRelationConstraintFields(constraint: ResolvedMonospaceSingleConstraint) {
	const constrainedFields: ResolvedMonospacePrimitiveField[] = [];
	const referencedFields: ResolvedMonospacePrimitiveField[] = [];

	for (const mapping of constraint.fieldMappings) {
		constrainedFields.push(mapping.constrainedField!);
		referencedFields.push(mapping.referencedField!);
	}

	return { constrainedFields, referencedFields };
}

/**
 * Get the fields which are backing either side of a relation field.
 *
 * The returned fields are viewed from the side of the relation passed in, `fields` being the primitive
 * fields that live on this side of the relation, `oppositeFields` living on the opposite collection.
 */
export function getRelationPrimitiveFields(relation: ResolvedMonospaceSingleRelationField): {
	fields: ResolvedMonospacePrimitiveField[];
	oppositeFields: ResolvedMonospacePrimitiveField[];
} {
	const { thisSideConstrained, constraint } = getRelationConstraint(relation);

	if (!constraint) {
		return {
			fields: [],
			oppositeFields: [],
		};
	}

	const { constrainedFields, referencedFields } = getRelationConstraintFields(constraint);

	return {
		fields: thisSideConstrained ? constrainedFields : referencedFields,
		oppositeFields: thisSideConstrained ? referencedFields : constrainedFields,
	};
}
